import { unwrapResult } from "@reduxjs/toolkit";
import { useRouter } from "next/router";
import { useState } from "react";
import { useSelector } from "react-redux";
import type {
  CustomDomainSsoErrorResponse,
  CustomDomainSsoTokenResponse,
  SsoValidationErrorResponse,
  SsoValidationResponse,
} from "src/types";

import {
  getIsAuthenticationLoading,
  identifyAttendee,
  isSimpleFlowSelector,
  isUserRegisteredSelector,
  receptionUrlSelector,
  RegistrationResponse,
  SimpleFlowResponse,
  verifySimpleFlow,
} from "@/features/authentication/authentication-reducer";
import {
  fetchEventAuthDetails,
  getEventAuthDetailsLoaded,
  getIsCustomDomainEvent,
  getRequireSsoLogin,
} from "@/features/event-auth-details/event-auth-details-reducer";
import { getUser } from "@/features/user/user-reducer";
import { useAppDispatch } from "@/redux/store";
import endpoints from "@/utils/endpoints";
import { authenticatedFetch, fetchJSON } from "@/utils/fetchJSON";
import { isAttendeesPage } from "@/utils/is-attendees-page";
import { isCustomDomainSsoError, isSsoValidationError } from "@/utils/is-error";
import logger from "@/utils/logger";
import { redirectWithLoopGuard } from "@/utils/redirect-with-loop-guard";

const get = endpoints();

export const SSO_TOKEN_LOCALSTORAGE_KEY = "sso_token";
export const SSO_CODE_PARAM_KEY = "sso_code";

type UseAuthenticationProps = {
  slug: string;
};

/**
 * Hook to be used to authenticate a user
 * @param slug - event slug to be authenticated against
 */
const useAuthentication = ({ slug }: UseAuthenticationProps) => {
  const router = useRouter();

  const isIndexPage = isAttendeesPage();
  let requireSsoLogin = useSelector(getRequireSsoLogin) && isIndexPage;
  let isCustomDomainEvent = useSelector(getIsCustomDomainEvent);
  const isEventAuthDetailsLoaded = useSelector(getEventAuthDetailsLoaded);

  const isHopinUserRegistered = useSelector(isUserRegisteredSelector);
  const isSimpleFlow = useSelector(isSimpleFlowSelector);
  const eventAccessUrl = useSelector(receptionUrlSelector);
  const isHopinAuthenticationLoading = useSelector(getIsAuthenticationLoading);
  const hopinUser = useSelector(getUser);

  const [isSsoCheckFinished, setIsSsoCheckFinished] = useState(false);
  const dispatch = useAppDispatch();

  const identifySso = async (slug, isCustomDomainEvent) => {
    const validateSso = async () => {
      try {
        const fetchHelper = isCustomDomainEvent
          ? authenticatedFetch.fetchJSON
          : fetchJSON;

        const options = { method: "POST", body: { event_slug: slug } } as any;

        if (!isCustomDomainEvent) {
          options.credentials = "include";
        }

        const response = await fetchHelper<
          SsoValidationResponse,
          SsoValidationErrorResponse
        >(get("HOPIN_SSO_CHECK"), { ...options });

        if (isSsoValidationError(response)) {
          throw new Error("SSO authentication error");
        }
        setIsSsoCheckFinished(true);
      } catch {
        window.localStorage.removeItem(SSO_TOKEN_LOCALSTORAGE_KEY);

        try {
          const params = new URLSearchParams(window.location.search);
          params.append("sso_o", "canvas");

          redirectWithLoopGuard(
            get("HOPIN_SSO_EVENT_LOGIN")(slug, `?${params.toString()}`)
          );
        } catch (redirectLoopError) {
          // If redirect loop, log and send to 404 page
          logger.error(redirectLoopError, {
            details: "Failed to load SSO protected event",
          });
          router.replace("/404");
        }
      }
    };

    const fetchCustomDomainSsoToken = async () => {
      const {
        [SSO_CODE_PARAM_KEY]: code,
        ...routerQueryParamsWithoutSSO
      } = router.query;

      if (code) {
        router.push(
          {
            pathname: router.pathname,
            query: routerQueryParamsWithoutSSO,
          },
          undefined,
          { shallow: true }
        );

        try {
          const response = await fetchJSON<
            CustomDomainSsoTokenResponse,
            CustomDomainSsoErrorResponse
          >(get("HOPIN_SSO_GET_TOKEN"), {
            method: "POST",
            body: {
              code,
            },
          });

          if (!isCustomDomainSsoError(response)) {
            window.localStorage.setItem(
              SSO_TOKEN_LOCALSTORAGE_KEY,
              response.token
            );
          }
        } catch (tokenExchangeError) {
          logger.error(tokenExchangeError, {
            details: "Failed to exchange SSO code for JWT token",
          });
        }
      }
    };

    if (isCustomDomainEvent) {
      await fetchCustomDomainSsoToken();

      if (window.localStorage.getItem(SSO_TOKEN_LOCALSTORAGE_KEY)) {
        authenticatedFetch.prepareHeaders({
          token: window.localStorage.getItem(SSO_TOKEN_LOCALSTORAGE_KEY),
        });

        await dispatch(verifySimpleFlow(slug));
      }
    }

    await validateSso();
  };

  const identifyHopinUser = async (): Promise<
    RegistrationResponse & SimpleFlowResponse
  > => {
    const identifyAttendeeResult = await dispatch(identifyAttendee(slug));
    const registrationStatus = unwrapResult(identifyAttendeeResult);

    const verifySimpleFlowResult = await dispatch(verifySimpleFlow(slug));
    const simpleFlowStatus = unwrapResult(verifySimpleFlowResult);

    return {
      ...registrationStatus,
      ...simpleFlowStatus,
    };
  };

  const identify = async () => {
    if (!isEventAuthDetailsLoaded) {
      const eventAuthDetailsResult = await dispatch(
        fetchEventAuthDetails(slug)
      );
      const eventAuthDetails = unwrapResult(eventAuthDetailsResult);

      requireSsoLogin = eventAuthDetails.requireSsoLogin && isIndexPage;
      isCustomDomainEvent = eventAuthDetails.customDomainEvent;
    }

    let userData = {
      isHopinUserRegistered,
      isSimpleFlow,
      eventAccessUrl,
    };

    if (!(isCustomDomainEvent && isIndexPage)) {
      const {
        registered: isHopinUserRegistered,
        is_simple_flow: isSimpleFlow,
        reception_url: eventAccessUrl,
      } = await identifyHopinUser();

      userData = {
        isHopinUserRegistered,
        isSimpleFlow,
        eventAccessUrl,
      };
    }

    if (requireSsoLogin) {
      await identifySso(slug, isCustomDomainEvent);
    }

    return userData;
  };

  const isSsoAuthenticationLoading =
    isIndexPage &&
    (!isEventAuthDetailsLoaded || (requireSsoLogin && !isSsoCheckFinished));

  const isAuthenticationLoading =
    !isEventAuthDetailsLoaded ||
    isHopinAuthenticationLoading ||
    isSsoAuthenticationLoading;

  return {
    hopinUser,
    isHopinUserRegistered,
    isSimpleFlow,
    eventAccessUrl,
    isAuthenticationLoading,
    isHopinAuthenticationLoading,
    isSsoAuthenticationLoading,
    identify,
  };
};

export { useAuthentication };
